import NetworkService from './networkService';

export interface AvailablePhoneNumber {
  phoneNumber: string;
  friendlyName: string;
  locality?: string;
  region?: string;
  postalCode?: string;
  isoCountry?: string;
}

export interface TwilioConfig {
  phoneNumber: string;
  phoneNumberSid: string;
  messagingServiceSid: string;
  campaignId?: string | null;
  purchasedAt: any;
  status: string;
  locality?: string;
  region?: string;
}

export interface PurchaseNumberResponse {
  success: boolean;
  phoneNumber: string;
  messagingServiceSid: string;
  message: string;
}

export interface ReleaseNumberResponse {
  success: boolean;
  message: string;
}

class TwilioService {
  private networkService: NetworkService;

  constructor() {
    this.networkService = new NetworkService();
  }

  /**
   * Search for available phone numbers by area code
   */
  async searchAvailableNumbers(areaCode: string, country: string = 'US'): Promise<AvailablePhoneNumber[]> {
    try {
      console.log('🔍 [TwilioService] Searching for available numbers:', { areaCode, country });
      // Build URL with query parameters
      const url = `/twilio/available-numbers?areaCode=${encodeURIComponent(areaCode)}&country=${encodeURIComponent(country)}`;
      const response = await this.networkService.get(url);
      console.log('📞 [TwilioService] Found numbers:', response.data);
      return response.data || [];
    } catch (error: any) {
      console.error('❌ [TwilioService] Error searching numbers:', error);
      throw new Error(error.response?.data?.message || 'Failed to search for phone numbers');
    }
  }

  /**
   * Purchase a phone number for the company
   */
  async purchaseNumber(phoneNumber: string): Promise<PurchaseNumberResponse> {
    try {
      console.log('💳 [TwilioService] Purchasing phone number:', phoneNumber);
      const response = await this.networkService.post('/twilio/purchase-number', {
        phoneNumber
      });
      console.log('✅ [TwilioService] Purchase successful:', response.data);
      return response.data;
    } catch (error: any) {
      console.error('❌ [TwilioService] Error purchasing number:', error);
      throw new Error(error.response?.data?.message || 'Failed to purchase phone number');
    }
  }

  /**
   * Get company's Twilio configuration
   */
  async getCompanyTwilioConfig(): Promise<TwilioConfig | null> {
    try {
      console.log('📋 [TwilioService] Fetching company Twilio config');
      const response = await this.networkService.get('/twilio/config');
      console.log('📊 [TwilioService] Config:', response.data);
      return response.data || null;
    } catch (error: any) {
      // Check for the specific TWILIO_NOT_CONFIGURED error code
      if (error.code === 'TWILIO_NOT_CONFIGURED') {
        console.log('ℹ️ [TwilioService] No Twilio config found for company (TWILIO_NOT_CONFIGURED)');
        // Re-throw with the error code preserved
        throw error;
      }
      if (error.status === 404) {
        console.log('ℹ️ [TwilioService] No Twilio config found for company');
        return null;
      }
      console.error('❌ [TwilioService] Error getting config:', error);
      throw error;
    }
  }

  /**
   * Release company's phone number
   */
  async releaseNumber(): Promise<ReleaseNumberResponse> {
    try {
      console.log('🗑️ [TwilioService] Releasing phone number');
      const response = await this.networkService.delete('/twilio/number');
      console.log('✅ [TwilioService] Number released:', response.data);
      return response.data;
    } catch (error: any) {
      console.error('❌ [TwilioService] Error releasing number:', error);
      throw new Error(error.response?.data?.message || 'Failed to release phone number');
    }
  }

}

export default TwilioService;
